/* eslint-disable no-param-reassign */
/**
 * 函数调用链上报模块
 */

import cookies from './cookies';
import { stringify } from './querystring';

interface baseInfo {
  attaid: string;
  token: string;
  userId: string;
  ua: string;
}

export interface stackItem {
  fnName: string;
  args: any[];
  result: any;
  context: string; // 上下文
  timestamp: string; // 毫秒级执行时间戳，但同步代码执行时会存在时间戳相同的情况，在 mergeStack 中会进行处理
  execTime: number;
}

export enum trackerEnv {
  parent = 'parent',
  child = 'child'
}

/**
 * 用于收到组件层消息后执行的回调函数
 * @example
 *
 */
export function trackerCb(childStack: stackItem[], childIsEnd: boolean): any {
  const parentStack = ParentStack.instance;
  parentStack.receive(childStack, childIsEnd);
}

let baseReportInfo: baseInfo = null;

async function reportAtta(stacks: stackItem[]) {
  if (!baseReportInfo) {
    baseReportInfo = {
      attaid: '0d900032162',
      token: '5887416346',
      userId: cookies.getItem('vuserid') || cookies.getItem('vqq_vuserid'),
      ua: navigator.userAgent,
    };
  }

  // 批量上报
  stacks.forEach((stack) => {
    const reportData = {
      ...baseReportInfo,
      ...stack,
      ...{
        args: JSON.stringify(stack.args),
        result: JSON.stringify(stack.result),
      },
      _dc: Math.random(),
    };

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '//h.trace.qq.com/kv', true);
    xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
    xhr.send(`${stringify(reportData).replace(/^\?/, '')}`);
  });
}

async function handleStack(stack: stackItem[]): Promise<stackItem[]> {
  // stack中保存的函数信息的返回值可能是promise
  await Promise.all(stack.map(info => info.result)).then((result) => {
    stack.forEach((info, idx) => {
      info.args = info.args.map(arg => handleValue(arg));
      info.result = handleValue(result[idx]);
    });
  })
    .catch(() => {
      stack.forEach((info) => {
        info.args = info.args.map(arg => handleValue(arg));
        info.result = handleValue(info.result);
      });
    });
  return stack;
}

function handleValue(value: any) {
  const baseTypes = ['Object', 'String', 'Array', 'Boolean', 'Null', 'Undefined', 'Function'];
  // 由构造函数new出来的对象
  if (
    typeof value === 'object'
    && value !== null
    && baseTypes.indexOf(value.constructor.name) === -1
  ) {
    return `instance: ${value.constructor.name}`;
  }
  // 函数
  if (typeof value === 'function') {
    return `function: ${value.name}`;
  }
  // 普通对象
  if (typeof value === 'object' && value !== null && value.constructor.name === 'Object') {
    const newVal: any = {};
    Object.keys(value).forEach((key) => {
      newVal[key] = handleValue(value[key]);
    });
    return newVal;
  }
  return value;
}

// 根据时间戳合并 parentStack 和 childStack
function mergeStack(parentStack: stackItem[], childStack: stackItem[]) {
  /**
   * 需要处理毫秒级时间戳相同的情况
   * {
   *    '122': [stackItem],
   *    '123': [stackItem, stackItem]
   * }
   */
  const timeMap: { [key: string]: stackItem[] } = {};

  // 保存时间戳数组 [122, 123]
  const timeArr: string[] = [];

  parentStack.forEach((info) => {
    if (!timeMap[info.timestamp]) {
      timeMap[info.timestamp] = [info];
    } else {
      timeMap[info.timestamp].push(info);
    }

    if (!timeArr.includes(info.timestamp)) {
      timeArr.push(info.timestamp);
    }
  });

  childStack.forEach((info) => {
    if (!timeMap[info.timestamp]) {
      timeMap[info.timestamp] = [info];
    } else {
      timeMap[info.timestamp].push(info);
    }

    if (!timeArr.includes(info.timestamp)) {
      timeArr.push(info.timestamp);
    }
  });

  const mergedTimeArr = timeArr.sort((a, b) => Number(a) - Number(b));

  let mergedStack: stackItem[] = [];
  mergedTimeArr.forEach((s) => {
    mergedStack = mergedStack.concat(timeMap[s]);
  });

  return mergedStack;
}

function getCurrentMsTime() {
  if ('function' === typeof performance?.now) {
    return performance.now();
  }
  return +new Date();
}

abstract class Stack {
  public stack: stackItem[] = []; // 保存函数调用链信息
  public isEnd: boolean; // 调用链是否结束
  protected timer: any; // 超时器
  protected timeout: number; // 超时时长

  protected constructor(timeout: number) {
    this.timeout = timeout;
    // 如果执行超时，则标记结束，send当前执行堆栈
    this.timer = setTimeout(() => {
      this.isEnd = true;
      this.send(false);
    }, this.timeout);
  }

  public pushStack(info: stackItem, isEnd: boolean) {
    // 如果标记结束则不再push
    if (this.isEnd) {
      return;
    }
    this.stack.push(info);

    // 当前是最后一个stack，需要标记一下结束，send出去
    if (!isEnd) {
      return;
    }
    this.isEnd = true;
    clearTimeout(this.timer);
    this.send(true);
  }

  protected abstract send(isEnd: boolean): void;
}

// 用于管理H5播放层的栈
class ParentStack extends Stack {
  private static single: ParentStack; // 单例
  private childStack: stackItem[] = []; // 互动层函数调用链信息
  private childIsEnd: boolean; // 互动层调用链是否结束

  public static get instance() {
    if (this.single) {
      return this.single;
    }
    return (this.single = new ParentStack(3000));
  }

  /**
   * 接受互动组件层消息
   * @param childStack 互动层栈
   * @param childIsEnd 互动层是否到调用链结尾
   */
  public async receive(childStack: stackItem[], childIsEnd: boolean) {
    this.childStack = this.childStack.concat(childStack);
    if (!childIsEnd) {
      return;
    }
    this.childIsEnd = true;
  }

  protected async send(isEnd: boolean) {
    if (isEnd) {
      this.timer = setTimeout(() => {
        this.report();
      }, this.timeout);
    } else {
      this.report();
    }
  }

  private async report() {
    this.stack = await handleStack(this.stack);
    if (this.childStack) {
      const mergedStack = mergeStack(this.stack, this.childStack);
      reportAtta(mergedStack);
    } else {
      reportAtta(this.stack);
    }
  }
}

// 用于管理互动组件层的栈
class ChildStack extends Stack {
  private static single: ChildStack; // 单例

  public static get instance() {
    if (this.single) {
      return this.single;
    }
    return (this.single = new ChildStack(1000));
  }

  // 发送消息到播放层
  protected async send(isEnd: boolean) {
    this.stack = await handleStack(this.stack);
    window.iBridge?.childBridge.invoke({
      ns: 'tracker',
      method: 'send',
      data: this.stack,
      isEnd,
    });
  }
}

const stackInstance = new Map();
stackInstance.set(trackerEnv.parent, () => ParentStack.instance);
stackInstance.set(trackerEnv.child, () => ChildStack.instance);

/**
 * [方法装饰器] 工厂
 * @param isEnd 是否结束调用链
 * @param env 'parent' / 'child' 当前装饰器作用于播放层还是互动层
 */
export function methodTracker(isEnd: boolean, env: trackerEnv) {
  /**
   * 方法装饰器
   * @param target 被装饰的方法所在对象的原型
   * @param fnName 方法名称
   * @param fnDescriptor 方法的属性描述符
   */
  return function (
    target: Object,
    fnName: string,
    fnDescriptor: PropertyDescriptor,
  ): PropertyDescriptor {
    const fn = fnDescriptor.value;

    fnDescriptor.value = function (...args: any[]) {
      const stackItem: stackItem = {
        fnName,
        args,
        result: null,
        context: target.constructor.name,
        timestamp: `${Number(new Date())}${new Date().getMilliseconds()}`,
        execTime: getCurrentMsTime(),
      };
      const stackFn = stackInstance.get(env);
      stackFn().pushStack(stackItem, isEnd);
      const result = fn.apply(this, args);
      stackItem.result = result;
      (async (res: any) => {
        if (res?.then) {
          try {
            await Promise.all([res]);
          } catch (e) {
            stackItem.result = 'reject';
          }
        }
        stackItem.execTime = getCurrentMsTime() - stackItem.execTime;
      })(result);

      return result;
    };

    return fnDescriptor;
  };
}

/**
 * [类装饰器] 工厂
 * @param isEnd 是否结束调用链
 * @param env 'parent' / 'child' 当前装饰器作用于播放层还是互动层
 * @param include 需要装饰的方法名
 * @param exclude 不需要装饰的方法名
 * @notice include 和 exclude 同时使用时，以include为准
 */
export function classTracker(
  isEnd: boolean,
  env: trackerEnv,
  include: string[] = [],
  exclude: string[] = [],
) {
  const stackFn = stackInstance.get(env);

  /**
   * 类装饰器
   * @param target 类的构造函数
   */
  return function<T extends new (...args: any[]) => {}>(target: T): T {
    // 根据需要装饰类方法
    const propertyNames: string[] = Object.getOwnPropertyNames(target.prototype);
    let needDecorateNames: string[];
    if (include.length > 0) {
      needDecorateNames = propertyNames.filter(name => ~include.indexOf(name));
    } else if (include.length === 0 && exclude.length > 0) {
      needDecorateNames = propertyNames.filter(name => !~exclude.indexOf(name));
    } else {
      needDecorateNames = propertyNames;
    }

    needDecorateNames.forEach((propertyName) => {
      if (propertyName !== 'constructor') {
        const descriptor = Object.getOwnPropertyDescriptor(target.prototype, propertyName);
        // 保存旧的方法
        const originalFn = descriptor.value;

        descriptor.value = function (...args: any[]) {
          const stackItem: stackItem = {
            fnName: propertyName,
            args,
            result: null,
            context: target.name,
            timestamp: `${Number(new Date())}`,
            execTime: getCurrentMsTime(),
          };
          stackFn().pushStack(stackItem, isEnd);
          const result = originalFn.apply(this, args);
          stackItem.result = result;
          (async (res: any) => {
            if (res?.then) {
              try {
                await Promise.all([res]);
              } catch (e) {
                stackItem.result = 'reject';
              }
            }
            stackItem.execTime = getCurrentMsTime() - stackItem.execTime;
          })(result);

          return result;
        };
        Object.defineProperty(target.prototype, propertyName, descriptor);
      }
    });

    // 处理构造函数
    class NewConstructor extends target {
      public constructor(...args: any[]) {
        const stackItem: stackItem = {
          fnName: `constructor: ${target.name}`,
          args,
          result: `instance: ${target.name}`,
          context: target.name,
          timestamp: `${Number(new Date())}`,
          execTime: getCurrentMsTime(),
        };
        stackFn().pushStack(stackItem, isEnd);
        super(...args);
        stackItem.execTime = getCurrentMsTime() - stackItem.execTime;
      }
    }

    return NewConstructor;
  };
}
